<template>
    <CollapsibleWidget :is-opened="isOpen" collapsed-height="47" @onChange="toggleOpen">
        <template #name>{{ editable.label }}</template>

        <template #controls>
            <EditableRestrictionControl v-if="showRestrictionControl" :editable="editable" />
        </template>

        <div class="deliverable-values editable-widget__body">
            <div v-if="readOnly" class="editable-widget__body--readonly" title="Read Only" @click.stop.prevent></div>
            <ExpandableTextInput
                class="deliverable-value-item"
                :label="EditableGroupScopeLabel"
                :model-value="editableValue"
                :overwrite="getOverwrite(editable, groupsAffectingEditable, null)"
                :scope="OverwriteScope.EditableGroup"
                placeholder="Enter value"
                :lockable="false"
                @input="onValueChange($event, null)"
                @restore="onValueRestore(null)"
                @updateSettings="onUpdateSettings({ ...$event, templateId: null })"
            >
                <template v-if="editableValue" #icons>
                    <a target="_blank" :href="editableValue" @click.stop>
                        <Icon class="editable-url-widget__link-icon" type="ios-link" />
                    </a>
                </template>
            </ExpandableTextInput>

            <ExpandableTextInput
                v-for="template in templatesWithValues"
                v-show="isOpen"
                :key="template.id"
                class="deliverable-value-item"
                :label="template.label"
                :label-color="template.labelColor"
                :scope="OverwriteScope.EditableGroupTemplate"
                :model-value="template.editableValue"
                :overwrite="template.overwrite"
                placeholder="Enter value"
                @input="onValueChange($event, template.id)"
                @removeOverwrite="onRemoveOverwrite"
                @restore="onValueRestore(template.id)"
                @updateSettings="onUpdateSettings({ ...$event, templateId: template.id })"
            >
                <template v-if="template.editableValue" #icons>
                    <a target="_blank" :href="template.editableValue" @click.stop>
                        <Icon class="editable-url-widget__link-icon" type="ios-link" />
                    </a>
                </template>
            </ExpandableTextInput>
        </div>
    </CollapsibleWidget>
</template>

<script lang="ts">
import { defineComponent, toRef, type PropType } from "vue";
import { OverwriteScope } from "shared-utils/enums/overwrites";
import type { CSSSettings, Editable } from "shared-types";

import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";
import ExpandableTextInput from "@/components/Campaign/ExpandableTextInput.vue";
import EditableRestrictionControl from "@/components/Campaign/Editables/EditableRestrictionControl.vue";

import { useToggleEditableItem } from "@/composables/useToggleEditableItem";
import { useEditable } from "@/composables/useEditable";
import { useEditableMethods } from "@/composables/useEditableMethods";
import { useOverwrite } from "@/composables/useOverwrite";
import { useEditor } from "@/composables/useEditor";

import { EditableGroupScopeLabel } from "@/enums/overwrites";

export default defineComponent({
    name: "EditableTextWidget",
    components: {
        EditableRestrictionControl,
        ExpandableTextInput,
        CollapsibleWidget
    },

    props: {
        editable: {
            type: Object as PropType<Editable>,
            required: true
        },

        groupsAffectingEditable: {
            type: Array as PropType<string[]>,
            required: true
        },

        allExpanded: {
            type: Boolean,
            default: true
        },

        readOnly: {
            type: Boolean,
            default: false
        },

        showRestrictionControl: {
            type: Boolean,
            default: true
        }
    },

    setup(props, { emit }) {
        const allExpanded = toRef(props, "allExpanded");
        const editable = toRef(props, "editable");

        const { isOpen, toggleOpen } = useToggleEditableItem({ allExpanded });
        const { getOverwrite, removeOverwrite } = useOverwrite();
        const { getEditableValue, getLabelColor, update, restore, updateSettings } = useEditableMethods({ emit });
        const { templateLabels } = useEditable({ editable });
        const { selectedLanguage } = useEditor();

        return {
            isOpen,
            toggleOpen,
            getOverwrite,
            getEditableValue,
            getLabelColor,
            templateLabels,
            updateSettings,
            update,
            restore,
            removeOverwrite,
            selectedLanguage
        };
    },

    data() {
        return {
            EditableGroupScopeLabel,
            OverwriteScope
        };
    },

    computed: {
        editableValue() {
            return this.getEditableValue(this.editable, this.groupsAffectingEditable, null) as string | null;
        },

        templatesWithValues() {
            return Object.keys(this.templateLabels).map(templateId => ({
                editableValue: this.getEditableValue(this.editable, this.groupsAffectingEditable, templateId) as
                    | string
                    | null,
                id: templateId,
                label: this.templateLabels[templateId],
                labelColor: this.getLabelColor(this.editable, this.groupsAffectingEditable, templateId),
                overwrite: this.getOverwrite(this.editable, this.groupsAffectingEditable, templateId)
            }));
        }
    },

    methods: {
        onValueChange(value: string, templateId: string | null) {
            this.update({
                editable: this.editable,
                updateData: {
                    value
                },
                groupsAffectingEditable: this.groupsAffectingEditable,
                templateId
            });
        },

        onRemoveOverwrite(overwriteId: string) {
            this.removeOverwrite(overwriteId);
        },

        onValueRestore(templateId: string | null) {
            this.restore({
                editable: this.editable,
                templateId,
                language: this.selectedLanguage
            });
        },

        onUpdateSettings({
            settings,
            newSettings,
            templateId
        }: {
            settings: CSSSettings;
            newSettings: CSSSettings;
            templateId: string | null;
        }) {
            const mergedSettings = {
                ...settings,
                ...newSettings
            };
            this.updateSettings({
                editable: this.editable,
                settings: mergedSettings,
                templateId,
                groupsAffectingEditable: this.groupsAffectingEditable
            });
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.editable-url-widget__link-icon {
    color: $cmp-dark-font-color;

    &:hover {
        color: $cmp-dark-font-color;
    }
}
</style>
